// Modifica el componente TiempoProcesamientoWidget.js

import React, { useState, useEffect } from 'react';
import { Spinner } from 'reactstrap';
import { APIClient } from "../../helpers/api_helper";

const TiempoProcesamientoWidget = ({ identificador }) => {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const obtenerDatos = async () => {
      try {
        setLoading(true);
        const api = new APIClient();
        
        // Usar el cliente API que ya tiene configurada la autenticación
        const response = await api.get(`/solicitud/${identificador}/tiempo-transcurrido`);
        
        // Verificar la estructura de la respuesta y hacer console.log para depuración
        //console.log("Respuesta del endpoint:", response);
        
        if (response && response.data) {
          setDatos(response.data.data);
        } else {
          setError('Formato de respuesta inesperado');
        }
      } catch (err) {
        setError(`Error: ${err.message}`);
        console.error("Error completo:", err);
      } finally {
        setLoading(false);
      }
    };

    if (identificador) {
      obtenerDatos();
      
      // Actualizar cada 5 minutos
      const intervalo = setInterval(obtenerDatos, 300000);
      return () => clearInterval(intervalo);
    }
  }, [identificador]);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner color="primary" size="sm" /> Cargando información...
      </div>
    );
  }
  
  if (error) {
    return <div className="text-danger">{error}</div>;
  }
  
  if (!datos) return null;

  // Extraer datos de la respuesta
  const tiempoTranscurrido = datos.tiempo_transcurrido || {};
  const tiempoMaximo = datos.tiempo_maximo || {};
  const tiempoRestante = datos.tiempo_restante || {};
  const porcentaje = datos.porcentaje || 0;
  const estatus = datos.estatus || 'No disponible';
  const tipo = datos.tipo || 'No disponible';
  const esRenovacion = datos.es_renovacion || false;
  const fechaObjetivo = datos.fecha_objetivo || null;
  const ultimaEntrega = datos.ultima_entrega || null;
  const numeroEntregas = datos.numero_entregas || 0;

  // Determinar la clase de color basada en el porcentaje
  const getColorClass = () => {
    if (datos.color === "success") return "bg-success";
    if (datos.color === "warning") return "bg-warning";
    if (datos.color === "danger") return "bg-danger";
    return "bg-primary";
  };

  // Formatear fecha para mostrar
  const formatearFecha = (fechaISO) => {
    if (!fechaISO) return '-';
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  // VISUALIZACIÓN PARA AGUDAS
  if (tipo === "Aguda") {
    return (
      <div className="tiempo-procesamiento-widget">
        <div className="d-flex justify-content-end mb-2">
          <span className="badge bg-soft-primary">{tipo}</span>
        </div>
        
        <div className="progress animated-progress custom-progress progress-label mb-2">
          <div
            className={`progress-bar ${getColorClass()}`}
            role="progressbar"
            style={{ width: `${Math.min(100, porcentaje)}%` }}
            aria-valuenow={porcentaje}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div className="label">{Math.round(porcentaje)}%</div>
          </div>
        </div>
        
        <div className="d-flex justify-content-between">
          <small className="text-muted">
            Transcurrido: {tiempoTranscurrido.horas || 0}h {tiempoTranscurrido.minutos || 0}m
          </small>
          <small className="text-muted">
            Máximo: {tiempoMaximo.horas || 0}h
          </small>
        </div>
        
        {porcentaje >= 100 && (
          <div className="alert alert-danger mt-2 py-1 px-2 mb-0">
            <i className="ri-error-warning-line me-1"></i>
            <small>Tiempo excedido. Se requiere atención inmediata.</small>
          </div>
        )}
      </div>
    );
  }

  // VISUALIZACIÓN PARA CRÓNICAS - PRIMERA ENTREGA
  if (tipo === "Cronica" && !esRenovacion) {
    return (
      <div className="tiempo-procesamiento-widget">
        <div className="d-flex justify-content-end mb-2">
          <span className="badge bg-soft-info">{tipo} - 1ra Entrega</span>
        </div>
        
        <div className="progress animated-progress custom-progress progress-label mb-2">
          <div
            className={`progress-bar ${getColorClass()}`}
            role="progressbar"
            style={{ width: `${Math.min(100, porcentaje)}%` }}
            aria-valuenow={porcentaje}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div className="label">{Math.round(porcentaje)}%</div>
          </div>
        </div>
        
        <div className="d-flex justify-content-between">
          <small className="text-muted">
            Transcurrido: {tiempoTranscurrido.dias || 0}d {tiempoTranscurrido.horas_del_dia || 0}h
          </small>
          <small className="text-muted">
            Máximo: {tiempoMaximo.dias || 0} días
          </small>
        </div>
        
        {porcentaje >= 100 && (
          <div className="alert alert-danger mt-2 py-1 px-2 mb-0">
            <i className="ri-error-warning-line me-1"></i>
            <small>Tiempo excedido. Se requiere atención inmediata.</small>
          </div>
        )}
      </div>
    );
  }

  // VISUALIZACIÓN PARA CRÓNICAS - RENOVACIÓN (2da-6ta)
  if (tipo === "Cronica" && esRenovacion) {
    return (
      <div className="tiempo-procesamiento-widget">
        <div className="d-flex justify-content-end mb-3">
          <span className="badge bg-soft-info">{tipo} - Entrega #{numeroEntregas + 1}</span>
        </div>
        
        <div className="progress animated-progress custom-progress progress-label mb-3" style={{ height: '24px' }}>
          <div
            className={`progress-bar ${getColorClass()}`}
            role="progressbar"
            style={{ width: `${Math.min(100, porcentaje)}%` }}
            aria-valuenow={porcentaje}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div className="label">{Math.round(porcentaje)}%</div>
          </div>
        </div>
        
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="text-muted">
              <i className="ri-calendar-check-line me-1"></i>
              Próxima entrega:
            </small>
            <strong className="text-primary">{formatearFecha(fechaObjetivo)}</strong>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              <i className="ri-calendar-line me-1"></i>
              Última entrega:
            </small>
            <span className="text-muted">{formatearFecha(ultimaEntrega)}</span>
          </div>
        </div>
        
        <div className="d-flex justify-content-between">
          <small className="text-muted">
            Días restantes: <strong>{tiempoRestante.dias || 0} días</strong>
          </small>
          <small className="text-muted">
            Plazo: 30 días
          </small>
        </div>
        
        {porcentaje >= 100 && (
          <div className="alert alert-danger mt-2 py-1 px-2 mb-0">
            <i className="ri-error-warning-line me-1"></i>
            <small>Fecha de entrega excedida. Contactar al paciente.</small>
          </div>
        )}
      </div>
    );
  }

  // Fallback por si no coincide con ningún caso
  return null;
};

export default TiempoProcesamientoWidget;